import { useEffect } from 'react';

import { Flex } from '@grupoboticario/flora-react';
import { InformationCircleIcon } from '@grupoboticario/flora-react-icons';

import { ErrorBoundaries, ResponsiveFloraTypography } from 'components';
import { getFormattedPrice, getLongDateTime } from 'helpers';
import { useIncentiveCampaign } from 'hooks';
import { isPositionTextVisible } from 'pages/IncentiveCampaign/helpers/is-position-text-visible';

import Styles from './RankingResults.styles';
import { useLoyaltyIncentiveCampaign } from '../../hooks';

export const RankingResults = () => {
  const {
    data: incentiveCampaign,
    isError: campaignError,
    isLoading: campaignLoading,
    campaignId,
  } = useLoyaltyIncentiveCampaign();
  const useResults = useIncentiveCampaign.ranking.useResults;
  const {
    data: rankingResults,
    refetch,
    isLoading: rankingLoading,
    isError: rankingError,
  } = useResults(campaignId ?? '');
  const isLoading = campaignLoading || rankingLoading;
  const isError = campaignError || rankingError;

  const lastUpdate = rankingResults?.lastUpdate
    ? getLongDateTime(rankingResults?.lastUpdate)
    : null;

  useEffect(() => {
    if (campaignId && !isError) {
      refetch();
    }
  }, [refetch, campaignId, isError]);

  if (isLoading) return null;

  if (isError) {
    return (
      <ErrorBoundaries.ReloadPage
        title='Tivemos um problema para exibir o ranking.'
        data-autotagging-flora-section={document}
      />
    );
  }

  return (
    <Flex direction='column' gap='$8'>
      <Flex direction='column' gap='$6'>
        <ResponsiveFloraTypography fontSize='title' fontWeight='bold' as='h2'>
          Premiação TOP {incentiveCampaign?.limit}
        </ResponsiveFloraTypography>

        <Flex direction='column' gap='$4'>
          <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
            Durante o período da campanha, as TOP {incentiveCampaign?.limit} pessoas revendedoras
            que mais investiram em presentes de Natal e/ou perfumaria O.U.i conquistaram prêmios
            incríveis da campanha. <br />
            <br />
            {isPositionTextVisible(rankingResults) ? (
              <>
                Parabéns, você é uma das pessoas revendedoras ganhadoras! Quer receber seu prêmio
                com rapidez e agilidade? Aproveite para atualizar seus dados de entrega aqui mesmo
                no Portal do Revendedor. Conheça o ranking com as pessoas ganhadoras:
              </>
            ) : (
              <>
                Infelizmente você não atingiu uma posição entre as TOP {incentiveCampaign?.limit} da
                sua faixa.
              </>
            )}
          </ResponsiveFloraTypography>
        </Flex>

        <Styles.RankingWrapper size='medium'>
          <Flex direction='column' gap='$4'>
            <Styles.TotalCard>
              <Flex direction='column' gap='$4' align='center'>
                <ResponsiveFloraTypography fontSize='subtitle' fontWeight='bold'>
                  Acumulado no período
                </ResponsiveFloraTypography>

                <ResponsiveFloraTypography
                  fontSize='subtitle'
                  color='$nonInteractiveEmphasis'
                  fontWeight='bold'
                >
                  {getFormattedPrice(rankingResults?.resellerPurchaseAmount ?? 0)}
                </ResponsiveFloraTypography>
              </Flex>
            </Styles.TotalCard>

            <Styles.InfoCard>
              <Flex gap='$2'>
                <Styles.InfoIcon>
                  <InformationCircleIcon size={20} color='$nonInteractiveAuxiliar' />
                </Styles.InfoIcon>

                <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
                  Para a premiação TOP {incentiveCampaign?.limit}, somamos as compras de produtos
                  elegíveis a partir do ciclo que você realizou a sua inscrição na campanha.
                </ResponsiveFloraTypography>
              </Flex>
            </Styles.InfoCard>
          </Flex>

          <Flex direction='column' gap='$1'>
            <Styles.TierCard>
              <ResponsiveFloraTypography fontWeight='bold'>
                TOP {incentiveCampaign?.limit}{' '}
                {rankingResults?.tierName && `- ${rankingResults.tierName}`}
              </ResponsiveFloraTypography>
            </Styles.TierCard>

            {isPositionTextVisible(rankingResults) ? (
              <Styles.WithPosition>
                <ResponsiveFloraTypography fontSize='bodyLargeShort' color='$backgroundPrimary'>
                  Você atingiu a posição {rankingResults?.position}!
                </ResponsiveFloraTypography>
              </Styles.WithPosition>
            ) : (
              <Styles.WithoutPosition>
                <ResponsiveFloraTypography fontSize='bodyLargeShort' color='$backgroundPrimary'>
                  Você não atingiu o TOP {incentiveCampaign?.limit} da sua faixa!
                </ResponsiveFloraTypography>
              </Styles.WithoutPosition>
            )}

            {rankingResults?.ranking?.length ? (
              <Styles.PositionsRankingWrapper>
                <Styles.PositionsRankingCard data-title>
                  <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                    Posição
                  </ResponsiveFloraTypography>

                  <Styles.ResellerIdLabel>
                    <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                      Código do(a) revendedor(a)
                    </ResponsiveFloraTypography>
                  </Styles.ResellerIdLabel>
                  <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                    Valor
                  </ResponsiveFloraTypography>
                </Styles.PositionsRankingCard>

                <Styles.PositionsList>
                  {rankingResults.ranking.map((rank, index) => (
                    <Styles.PositionsRankingCard key={`positions-card-${index}`}>
                      <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                        {rank.position}
                      </ResponsiveFloraTypography>
                      <Styles.ResellerIdValue>
                        <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                          {rank.resellerId}
                        </ResponsiveFloraTypography>
                      </Styles.ResellerIdValue>
                      <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                        {getFormattedPrice(rank.purchaseAmount)}
                      </ResponsiveFloraTypography>
                    </Styles.PositionsRankingCard>
                  ))}
                </Styles.PositionsList>
              </Styles.PositionsRankingWrapper>
            ) : null}

            <Styles.InfoCard>
              {lastUpdate ? (
                <Flex justify='space-between'>
                  <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
                    Atualização final da campanha
                  </ResponsiveFloraTypography>
                  <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
                    {lastUpdate}
                  </ResponsiveFloraTypography>
                </Flex>
              ) : (
                <></>
              )}
            </Styles.InfoCard>
          </Flex>
        </Styles.RankingWrapper>
      </Flex>
    </Flex>
  );
};
